'use client';
import cn from 'classnames';
import { ReactNode, useEffect } from 'react';
import reactToText from 'react-to-text';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

import WhiteFlower from 'public/img/icon/white-flower.svg';

import Link from '@/components/common/Link/Link';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';
import { useModal } from '@/components/hook/modal';
import { useCloset } from '@/components/hook/closet';
import { useUser } from '@/components/hook/user';
import { ClosetContextValue } from '@/components/context/ClosetContext/ClosetContext';
import { ModalContextValue } from '@/components/context/ModalContext/ModalContext';
import LoginModal from '@/components/modal/LoginModal/LoginModal';
import { LoginFormValues } from '@/components/form/LoginForm/LoginForm';

import { SIGNUP_ROUTES } from '@/const/signup';
import { CLOSET_SLOT_TIER_SLUG, ClosetValidationStatus } from '@/types/Closet';
import { User } from '@/types/User';
import { ProductDetailData } from '@/types/Product';
import { isPriveeFrozen } from '@/util/user_client';

import './AddToClosetSection.style.scss';
import { getSwapDateForTierInCloset } from './dataTools';
import { trackPdpUserState } from './productDetailAnalytics';

/** Retrieves the sub text that shows up under the "Add To Closet" button */
function getSubtext(
  closet: ClosetContextValue,
  closetValidationStatus: ClosetValidationStatus | null | undefined,
  modalTools: ModalContextValue,
  productData: ProductDetailData,
  user: User | undefined,
  onLoginSuccess: (values: LoginFormValues, user: User) => Promise<void>,
  productPath: string
): ReactNode {
  if (user?.isLocked) {
    return null;
  }

  if (!user?.status || closetValidationStatus === null) {
    return (
      <Text>
        <Link
          href='#'
          underline
          onClick={() => {
            modalTools.showModal(
              <LoginModal
                headline='Login'
                initialValues={{ username: '', password: '' }}
                onClose={modalTools.closeModal}
                onSubmitSuccess={(_: LoginFormValues, resp: unknown) => {
                  // Refresh closet validation status
                  onLoginSuccess(_, resp as User);
                  modalTools.closeModal();
                }}
                onNavigateClick={modalTools.closeModal}
              />,
              { topClassName: 'bg-gold' }
            );
          }}
        >
          Login
        </Link>{' '}
        or{' '}
        <Link
          href={SIGNUP_ROUTES.ACCOUNT}
          underline
        >
          Apply
        </Link>{' '}
        to borrow this item
      </Text>
    );
  }

  // Item is borrowed. Short circuit
  if (productData.borrowed) {
    return <Text>Add to your wishlist for back-in-stock notifications</Text>;
  }

  switch (closetValidationStatus) {
    case ClosetValidationStatus.TooEarly: {
      const swapData = getSwapDateForTierInCloset(
        productData.productTier.toLowerCase() as CLOSET_SLOT_TIER_SLUG,
        closet
      );
      if (swapData) {
        return <Text>Eligible to borrow in {swapData.remainingDays} days</Text>;
      }
      // A failure state just in case something goes horribly wrong
      return <Text>Eligible to borrow at your next swap date</Text>;
    }

    case ClosetValidationStatus.TooEarlyOrUpgrade:
    case ClosetValidationStatus.Upgrade:
    case ClosetValidationStatus.UpgradeOrReturn: {
      return (
        <Text>
          <Link
            href='/account/upgrade'
            underline
          >
            Upgrade
          </Link>{' '}
          to borrow this item
        </Text>
      );
    }

    case ClosetValidationStatus.Return: {
      return <Text>Return an item in your closet to borrow this item</Text>;
    }

    case ClosetValidationStatus.Resubscribe: {
      return (
        <Text>
          Your membership is currently frozen. Unfreeze on My Account to borrow.
        </Text>
      );
    }
    // NotApproved = 'ERR_NOT_APPROVED',
    case ClosetValidationStatus.NotApproved: {
      return <Text>Your membership is pending approval</Text>;
    }
    // ReapprovalNeeded = 'ERR_RE_APPROVAL',
    case ClosetValidationStatus.ReapprovalNeeded: {
      return (
        <Text>
          Your membership is not active.{' '}
          <Link
            href='/account/reactivate'
            underline
          >
            Reactivate your account.
          </Link>{' '}
        </Text>
      );
    }
    // PaymentNeeded = 'ERR_PAYMENT',
    case ClosetValidationStatus.PaymentNeeded: {
      return (
        <Text>
          Your payment method is invalid. Visit{' '}
          <Link
            href='/account/'
            underline
          >
            My Account
          </Link>{' '}
          to update your billing details.
        </Text>
      );
    }

    // EmptyCart = 'ERR_EMPTY_CART'
    case ClosetValidationStatus.EmptyCart: {
      return (
        <Text>
          <Link
            href='/cart'
            underline
            onClick={() => {
              // Storing the product path to localStorage so that we can return to the product once the user clears their cart
              localStorage.setItem('previousItemViewed', productPath);
            }}
          >
            Empty your cart
          </Link>{' '}
          to borrow this item
        </Text>
      );
    }

    // ItemInCart = 'ERR_ITEM_IN_CART'
    case ClosetValidationStatus.ItemInCart: {
      return (
        <Text>
          This item is already in your{' '}
          <Link
            href='/cart'
            underline
          >
            cart
          </Link>
        </Text>
      );
    }

    case ClosetValidationStatus.Generic: {
      return <Text>Item cannot be added to your cart</Text>;
    }

    // OK TO ADD
    default: {
      return null;
    }
  }
}

/** Retrieves the button state props for the "Add To Closet" button */
function getButtonState(
  closetValidationStatus: ClosetValidationStatus | null | undefined,
  productData: ProductDetailData,
  user: User | undefined
): Pick<AddToClosetSectionVanillaProps, 'disabled' | 'buttonText'> {
  if (productData.borrowed) {
    return {
      disabled: true,
      buttonText: 'CURRENTLY UNAVAILABLE'
    };
  }

  // User is unauthenticated OR Item is borrowed
  if (closetValidationStatus === null || user?.isLocked) {
    return {
      disabled: true,
      buttonText: 'ADD TO CLOSET'
    };
  }

  switch (closetValidationStatus) {
    // TODO: Item Unavailable: return this state with disabled button
    case ClosetValidationStatus.Upgrade:
    case ClosetValidationStatus.Resubscribe:
    case ClosetValidationStatus.NotApproved:
    case ClosetValidationStatus.ReapprovalNeeded:
    case ClosetValidationStatus.PaymentNeeded:
    case ClosetValidationStatus.Generic:
    case ClosetValidationStatus.EmptyCart:
    case ClosetValidationStatus.ItemInCart: {
      return {
        disabled: true,
        buttonText: 'ADD TO CLOSET'
      };
    }

    case ClosetValidationStatus.TooEarly:
    case ClosetValidationStatus.TooEarlyOrUpgrade:
    case ClosetValidationStatus.UpgradeOrReturn:
    case ClosetValidationStatus.Return:
    default: {
      return {
        disabled: false,
        buttonText: 'ADD TO CLOSET'
      };
    }
  }
}

type AddToClosetSectionVanillaProps = {
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  available?: boolean;
  buttonText: string;
  buttonSubText?: ReactNode;
  onButtonClick: () => void;
  showFlower?: boolean;
};

function AddToClosetSectionVanilla(props: AddToClosetSectionVanillaProps) {
  const {
    className = '',
    disabled = false,
    loading = false,
    available,
    buttonText,
    buttonSubText,
    onButtonClick,
    showFlower = true
  } = props;

  const isEmptySubText =
    buttonSubText === null ||
    (typeof buttonSubText === 'string' && buttonSubText.trim() === '');

  return (
    // data-available mirrors raw availability; the button text is overridden for sized
    // and frozen-Privée states, so it can't stand in for whether the item is buyable.
    <div
      className={cn('AddToClosetSection', className)}
      data-testid='pdp-add-to-closet'
      data-available={available}
    >
      <Button
        className='product-action-button cursor'
        size='large'
        onClick={onButtonClick}
        disabled={disabled}
      >
        {loading ? (
          <LoadingSpinner size={22} />
        ) : (
          <>
            {buttonText}
            {showFlower && (
              <Image
                className='product-action-button-flower'
                src={WhiteFlower}
                alt=''
                aria-hidden
                width={16}
                height={15}
              />
            )}
          </>
        )}
      </Button>
      <div
        className={cn('AddToClosetSection-subText', {
          empty: isEmptySubText
        })}
      >
        {buttonSubText}
      </div>
    </div>
  );
}

function trackPDPUserState(
  closetValidationStatus: ClosetValidationStatus | null | undefined,
  ctaSubText: string,
  product: { parent_sku: string; parent_sku_tier: string },
  productBorrowed: boolean | undefined,
  isPriveeProduct: boolean
) {
  let add_to_closet_state = false;
  let userState;

  switch (closetValidationStatus) {
    case ClosetValidationStatus.NotApproved: {
      userState = 'Not Approved';
      break;
    }

    case ClosetValidationStatus.PaymentNeeded: {
      userState = 'Payment Needed';
      break;
    }

    case ClosetValidationStatus.ReapprovalNeeded: {
      userState = 'Reapproval';
      break;
    }

    case ClosetValidationStatus.Return: {
      userState = 'Return';
      break;
    }

    case ClosetValidationStatus.TooEarly: {
      userState = 'Too Early';
      break;
    }

    case ClosetValidationStatus.TooEarlyOrUpgrade: {
      userState = 'Too Early or Upgrade';
      break;
    }

    case ClosetValidationStatus.Upgrade: {
      userState = 'Upgrade';
      break;
    }

    case ClosetValidationStatus.UpgradeOrReturn: {
      userState = 'Upgrade or Return';
      break;
    }

    case ClosetValidationStatus.Resubscribe: {
      userState = 'Resubscribe';
      break;
    }

    case ClosetValidationStatus.Generic: {
      userState = 'Generic';
      break;
    }

    // OK TO ADD
    default: {
      if (closetValidationStatus !== null) {
        add_to_closet_state = true;
        userState = 'Available';
      }
    }
  }

  if (productBorrowed) {
    add_to_closet_state = false;
    userState = 'Item Unavailable';
  }

  if (userState) {
    trackPdpUserState(userState, isPriveeProduct, {
      add_to_closet_state,
      ctaSubText,
      ...product
    });
  }
}

export type AddToClosetSectionProps = {
  className?: string;
  closetValidationStatus: ClosetValidationStatus | null | undefined;
  onButtonClick: () => void;
  onLoginSuccess: (values: LoginFormValues, user: User) => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  productData: ProductDetailData;
  hideSubText?: boolean;
  isPriveeProduct?: boolean;
};

export function AddToClosetSection(props: AddToClosetSectionProps) {
  const {
    className = '',
    closetValidationStatus,
    onButtonClick,
    onLoginSuccess,
    productData,
    loading = false,
    hideSubText = false
  } = props;
  const modalTools = useModal();
  const { value: closetValue } = useCloset();
  const { value: user } = useUser();
  const productPath = usePathname();

  const subText = getSubtext(
    closetValue,
    closetValidationStatus,
    modalTools,
    productData,
    user,
    onLoginSuccess,
    productPath
  );

  const buttonProps = getButtonState(closetValidationStatus, productData, user);

  // Frozen Privee user on a Privee product: show disabled unfreeze CTA
  const isPriveeProduct =
    productData.productTier.toLowerCase() === CLOSET_SLOT_TIER_SLUG.PRIVEE;
  const isUserPriveeFrozen = isPriveeProduct && isPriveeFrozen(user);
  if (isUserPriveeFrozen) {
    buttonProps.disabled = true;
    buttonProps.buttonText = 'UNFREEZE RÉSERVÉ TO BORROW';
  }

  const frozenPriveeSubText = isUserPriveeFrozen ? (
    <Text className='AddToClosetSection-frozenPriveeSubText'>
      Your membership is currently frozen.
      <br />
      Unfreeze on My Account to borrow.
    </Text>
  ) : null;

  const resolvedSubText = frozenPriveeSubText ?? subText;

  // If its sized update button text
  const isSized = !isUserPriveeFrozen && productData.sizeVariants;
  if (isSized) {
    buttonProps.buttonText = 'SELECT SIZE & ADD TO CLOSET';
  }

  const showFlower = !isSized && !isUserPriveeFrozen && !productData.borrowed;
  const ctaSubText = reactToText(resolvedSubText);
  let product = {
    parent_sku: productData.sku,
    parent_sku_tier: productData.productTier
  };
  let productBorrowed = productData.borrowed;

  // If loading is coming in from the very top,
  // Set the disabled state to true
  if (loading) {
    // Mutation is OK here since the object was just declared
    buttonProps.disabled = true;
  }

  useEffect(() => {
    if (!user?.status) {
      trackPdpUserState('Logged Out', isPriveeProduct, {
        add_to_closet_state: false,
        ctaSubText,
        ...product
      });
    } else {
      trackPDPUserState(
        closetValidationStatus,
        ctaSubText,
        product,
        productBorrowed,
        isPriveeProduct
      );
    }
  }, [closetValidationStatus, user]);

  return (
    <AddToClosetSectionVanilla
      className={className}
      available={!productData.borrowed}
      buttonSubText={
        loading
          ? ' '
          : hideSubText && !isUserPriveeFrozen
          ? null
          : resolvedSubText
      }
      onButtonClick={onButtonClick}
      showFlower={showFlower}
      {...buttonProps}
      loading={loading}
    />
  );
}

export default AddToClosetSection;
